// Modules
import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

// Hooks
import useNavigateWithParameters from 'hooks/external/useNavigateWithParameters'
import useSingleSignOn from './useSingleSignOn'

// Data
import products from 'data/products'

// Constants
import { FALLBACK_URL, IS_LIVE, IS_ON_PREMISE } from 'constants/environment'

// Data (Local)
const ssoHosts = ['dev-label.aixplain.com', 'localhost:4200', 'api-vision.apptek.com']

// Hook: External > Redirect
function useRedirect ({ willSkip } = {}) {
	// Data
	const { navigate } = useNavigate()
	const { navigateWithParameters } = useNavigateWithParameters()

	// Data (Memoized)
	const redirectURL = useMemo(() => {
		const storedRedirectURL = localStorage.getItem('redirectURL')
		const url = storedRedirectURL || FALLBACK_URL
		return !!url ? new URL(decodeURIComponent(url)) : null
	}, [])
	const tokenType = useMemo(() => {
		const product = Object
			.keys(products)
			.find(product => !!products[product]?.url ? (new URL(products[product].url)).hostname === redirectURL?.hostname : false)
		if (!!product) return products[product]?.tokenType
		const isInDomain = /^(.*\.)?aixplain\.com$/.test(redirectURL?.hostname)
		if (isInDomain) return 'platform'
		else if (IS_ON_PREMISE) return 'platform'
		else return null
	}, [redirectURL])

	// Data
	const isUsingSSO = ssoHosts.includes(redirectURL?.hostname)
	const isAllowedToRedirect = !!tokenType || (!IS_LIVE && !!redirectURL)
	const willRedirect = !willSkip && (isUsingSSO || isAllowedToRedirect)

	// Data
	useSingleSignOn({ redirectURL, willSkip: willSkip || !isUsingSSO }) // TODO: Remove when BE migrates to this app from old SSO

	// Effects
	useEffect(() => {
		if (!willSkip && !isUsingSSO && isAllowedToRedirect) {
			navigateWithParameters(
				!!redirectURL ? decodeURIComponent(redirectURL) : null,
				IS_LIVE ? tokenType : (tokenType || 'platform')
			)
		}
	}, [isAllowedToRedirect, isUsingSSO, navigateWithParameters, redirectURL, tokenType, willSkip])

	// Response
	return { willRedirect }
}

// Export
export default useRedirect
